import { createRouter, createWebHistory } from 'vue-router'
import { useWebSiteStore } from '@/stores/web-site.store'
import { refreshAllStoresAsync } from '@/composables/store'
import { updateDocumentTitle } from '@/lib/utils'
import { usePortalStore } from '@/stores/portal.store'

import HomePage from '@/views/protected/HomePage.vue'
import CatalogFormPage from '@/views/protected/CatalogFormPage.vue'
import ClipboardPage from '@/views/protected/sale/ClipboardPage'
import CompanyPage from '@/views/protected/registry/CompanyPage'
import DefaultLayout from '@/views/layouts/DefaultLayout.vue'
import DiscountCategoryPage from '@/views/protected/registry/DiscountCategoryPage'
// import DocumentSeriesPage from '@/views/protected/registry/DocumentSeriesPage.vue' US: 1241, waiting 979
import ElectronicInvoicePage from '@/views/protected/sale/ElectronicInvoicePage.vue'
import GarageEventSchedulerPage from '@/views/protected/garage/GarageEventSchedulerPage'
import LoginOidcPage from '@/views/public/LoginOidcPage'
import NotificationPage from '@/views/protected/NotificationPage'
import ProductPage from '@/views/protected/registry/ProductPage'
import QuotePage from '@/views/protected/garage/QuotePage'
import SaleDocumentPage from '@/views/protected/sale/SaleDocumentPage.vue'
import ServiceHistoryPage from '@/views/protected/garage/ServiceHistoryPage'
import SubjectDiscountPage from '@/views/protected/registry/SubjectDiscountPage.vue'
import SubjectPage from '@/views/protected/registry/SubjectPage.vue'
import SupplierBrandPage from '@/views/protected/registry/SupplierBrandPage'
import SupplierOrderPage from '@/views/protected/order/SupplierOrderPage'
import VehiclePage from '@/views/protected/registry/VehiclePage.vue'
import WarehouseDocumentPage from '@/views/protected/registry/WarehouseDocumentPage.vue'
import WorksheetKanbanBoardPage from '@/views/protected/garage/WorksheetKanbanBoardPage'
import WorkTimeEntryPage from '@/views/protected/garage/WorkTimeEntryPage'
import WorksheetPage from '@/views/protected/garage/WorksheetPage'
import MechanicPage from '@/views/protected/registry/MechanicPage'
import ServiceAttachmentPage from '@/views/protected/registry/ServiceAttachmentPage.vue'
import ServiceImportPage from '@/views/protected/registry/ServiceImportPage.vue'
import CrmPage from '@/views/protected/external/CrmPage.vue'

export const menuRoutesEnum = Object.freeze({
  HOME: '/',
  LOGIN: '/login',
  LOGIN_OIDC: '/login-oidc',
  ABOUT: '/about',
  COMPANY: '/registry/company',
  SUBJECTS: '/registry/subject',
  SUBJECT_DISCOUNTS: '/registry/subject-discount',
  DISCOUNT_CATEGORIES: '/registry/discount-category',
  DOCUMENT_SERIES: '/registry/document-series',
  QUOTES: '/garage/quote',
  WORKSHEETS: '/garage/worksheet',
  SERVICE_HISTORY: '/garage/service-history',
  WORKSHEET_KANBAN_BOARD: '/garage/worksheet-kanban-board',
  WORK_TIME_ENTRIES: '/garage/work-time-entry',
  GARAGE_EVENT_SCHEDULER: '/garage/event-scheduler',
  VEHICLES: '/registry/vehicle',
  NOTIFICATIONS: '/notification',
  SUPPLIER_ORDERS: '/order/supplier-order',
  PRODUCTS: '/registry/product',
  CLIPBOARDS: '/sale/clipboard',
  SALE_DIRECT: '/sale/direct',
  SALE_INVOICES: '/sale/invoice',
  SALE_COMMERCIAL: '/sale/commercial',
  SUPPLIER_BRANDS: '/registry/supplier-brand',
  MECHANICS: '/registry/mechanic',
  ELECTRONIC_INVOICE: '/sale/electronic-invoice',
  SERVICE_ATTACHMENTS: '/service/attachment',
  SERVICE_IMPORT: '/service/import',
  TABLES: '/table',
  CATALOG_FORM: '/catalog-form',
  WAREHOUSE_DOCUMENTS: '/registry/warehouse-document',
  CRM: '/crm',
})

export const menuRoutes = {
  'menu.home': menuRoutesEnum.HOME,
  'menu.login': menuRoutesEnum.LOGIN,
  'menu.about': menuRoutesEnum.ABOUT,
  'menu.company': menuRoutesEnum.COMPANY,
  'menu.subject': menuRoutesEnum.SUBJECTS,
  'menu.subject-discount': menuRoutesEnum.SUBJECT_DISCOUNTS,
  'menu.discount-category': menuRoutesEnum.DISCOUNT_CATEGORIES,
  'menu.document-series': menuRoutesEnum.DOCUMENT_SERIES,
  'menu.garage.quote': menuRoutesEnum.QUOTES,
  'menu.garage.worksheet': menuRoutesEnum.WORKSHEETS,
  'menu.garage.event-scheduler': menuRoutesEnum.GARAGE_EVENT_SCHEDULER,
  'menu.garage.service-history': menuRoutesEnum.SERVICE_HISTORY,
  'menu.garage.worksheet-kanban-board': menuRoutesEnum.WORKSHEET_KANBAN_BOARD,
  'menu.garage.work-time-entry': menuRoutesEnum.WORK_TIME_ENTRIES,
  'menu.vehicle.registry': menuRoutesEnum.VEHICLES,
  'menu.notification': menuRoutesEnum.NOTIFICATIONS,
  'menu.order.supplier': menuRoutesEnum.SUPPLIER_ORDERS,
  'menu.warehouse.document': menuRoutesEnum.WAREHOUSE_DOCUMENTS,
  'menu.product': menuRoutesEnum.PRODUCTS,
  'menu.sale.clipboard': menuRoutesEnum.CLIPBOARDS,
  'menu.sale.direct': menuRoutesEnum.SALE_DIRECT,
  'menu.sale.invoice': menuRoutesEnum.SALE_INVOICES,
  'menu.sale.commercial': menuRoutesEnum.SALE_COMMERCIAL,
  'menu.sale.electronic-invoice': menuRoutesEnum.ELECTRONIC_INVOICE,
  'menu.supplier-brand': menuRoutesEnum.SUPPLIER_BRANDS,
  'menu.mechanic': menuRoutesEnum.MECHANICS,
  'menu.service.attachment': menuRoutesEnum.SERVICE_ATTACHMENTS,
  'menu.service.import': menuRoutesEnum.SERVICE_IMPORT,
  'menu.table': menuRoutesEnum.TABLES,
  'menu.crm': menuRoutesEnum.CRM,
}

const routes = [
  {
    path: menuRoutesEnum.LOGIN, component: LoginOidcPage, meta: { allowAny: true, titleKey: 'menu.login' }
  },
  {
    path: menuRoutesEnum.LOGIN_OIDC, component: LoginOidcPage, meta: { allowAny: true, titleKey: 'menu.login' }
  },
  {
    path: menuRoutesEnum.CATALOG_FORM, component: CatalogFormPage, meta: { titleKey: 'menu.catalog.form' }
  },
  {
    path: menuRoutes['menu.home'],
    component: DefaultLayout,
    children: [
      { path: menuRoutesEnum.HOME, component: HomePage, meta: { titleKey: 'menu.home' } },
      { path: menuRoutesEnum.COMPANY, component: CompanyPage, meta: { titleKey: 'menu.company' } },
      { path: `${menuRoutesEnum.SUBJECTS}/:code?`, component: SubjectPage, meta: { titleKey: 'menu.subject' } },
      { path: menuRoutesEnum.SUBJECT_DISCOUNTS, component: SubjectDiscountPage, meta: { titleKey: 'menu.subject-discount' } },
      // { path: menuRoutesEnum.DOCUMENT_SERIES, component: DocumentSeriesPage, meta: { titleKey: 'menu.document-series' } }, US: 1241, waiting 979
      { path: menuRoutesEnum.DISCOUNT_CATEGORIES, component: DiscountCategoryPage, meta: { titleKey: 'menu.discount-category' } },
      { path: `${menuRoutesEnum.QUOTES}/:year?/:number?`, component: QuotePage, meta: { titleKey: 'menu.garage.quote' } },
      { path: menuRoutesEnum.QUOTES, component: QuotePage, meta: { titleKey: 'menu.garage.quote' } },
      { path: `${menuRoutesEnum.WORKSHEETS}/:year?/:number?`, component: WorksheetPage, meta: { titleKey: 'menu.garage.worksheet' } },
      { path: menuRoutesEnum.WORKSHEETS, component: WorksheetPage, meta: { titleKey: 'menu.garage.worksheet' } },
      { path: menuRoutesEnum.SERVICE_HISTORY, component: ServiceHistoryPage, meta: { titleKey: 'menu.garage.service-history' } },
      { path: menuRoutesEnum.GARAGE_EVENT_SCHEDULER, component: GarageEventSchedulerPage, meta: { titleKey: 'menu.garage.event-scheduler' } },
      { path: menuRoutesEnum.WORKSHEET_KANBAN_BOARD, component: WorksheetKanbanBoardPage, meta: { titleKey: 'menu.garage.worksheet-kanban-board' } },
      { path: menuRoutesEnum.WORK_TIME_ENTRIES, component: WorkTimeEntryPage, meta: { titleKey: 'menu.garage.work-time-entry', layoutClass: 'layout-grouping-grid' } },
      { path: menuRoutesEnum.SUPPLIER_ORDERS, component: SupplierOrderPage, meta: { titleKey: 'menu.order.supplier' } },
      { path: `${menuRoutesEnum.VEHICLES}/:code?`, component: VehiclePage, meta: { titleKey: 'menu.vehicle.registry' } },
      { path: menuRoutesEnum.NOTIFICATIONS, component: NotificationPage, meta: { titleKey: 'menu.notification' } },
      { path: menuRoutesEnum.PRODUCTS, component: ProductPage, meta: { titleKey: 'menu.product' } },
      { path: menuRoutesEnum.CLIPBOARDS, component: ClipboardPage, meta: { titleKey: 'menu.sale.clipboard' } },
      { path: menuRoutesEnum.SALE_DIRECT, component: SaleDocumentPage, meta: { titleKey: 'menu.sale.direct', type: menuRoutesEnum.SALE_DIRECT } },
      { path: menuRoutesEnum.SALE_INVOICES, component: SaleDocumentPage, meta: { titleKey: 'menu.sale.invoice', type: menuRoutesEnum.SALE_INVOICES } },
      { path: menuRoutesEnum.SALE_COMMERCIAL, component: SaleDocumentPage, meta: { titleKey: 'menu.sale.commercial', type: menuRoutesEnum.SALE_COMMERCIAL } },
      { path: menuRoutesEnum.ELECTRONIC_INVOICE, component: ElectronicInvoicePage, meta: { titleKey: 'menu.sale.electronic-invoice' } },
      { path: menuRoutesEnum.SERVICE_ATTACHMENTS, component: ServiceAttachmentPage, meta: { titleKey: 'menu.service.attachment' } },
      { path: menuRoutesEnum.SERVICE_IMPORT, component: ServiceImportPage, meta: { titleKey: 'menu.service.import' } },
      { path: menuRoutesEnum.SUPPLIER_BRANDS, component: SupplierBrandPage, meta: { titleKey: 'menu.supplier-brand' } },
      { path: menuRoutesEnum.MECHANICS, component: MechanicPage, meta: { titleKey: 'menu.mechanic' } },
      { path: menuRoutesEnum.WAREHOUSE_DOCUMENTS, component: WarehouseDocumentPage, meta: { titleKey: 'menu.warehouse.document' } },
      { path: menuRoutesEnum.CRM, component: CrmPage, meta: { titleKey: 'menu.crm', layoutClass: 'layout-external-frame' } },
    ]
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach(async(to, from, next) => {
  const webSiteStore = useWebSiteStore()

  if (webSiteStore.allowAutoLogin && !to.meta.allowAny) {
    webSiteStore.$hydrate({ runHooks: false })

    if (webSiteStore.refreshToken != null && webSiteStore.accessToken != null) {
      await webSiteStore.autoLoginAsync()
    }
  }

  if (webSiteStore.isLogged()) {
    await refreshAllStoresAsync(true)
  }

  for (const entry of webSiteStore.menuEntries) {
    if (to.path === menuRoutes[entry.code] && entry.claim) {
      if (!webSiteStore.hasClaim(entry.claim)) { return next(from) }
      break
    }
  }

  if (!Object.values(menuRoutes).some((path) => to.path.startsWith(path))) {
    next(menuRoutes['menu.home'])
    return
  }

  if (webSiteStore.isLogged()) {
    to.path === menuRoutes['menu.login'] ? next(menuRoutes['menu.home']) : next()
    return
  }

  to.meta.allowAny ? next() : next('/login')
})

router.afterEach((to) => {
  const titleKey = to.meta.titleKey
  updateDocumentTitle(titleKey)
})

const routesByEntityName = {
  'ClipboardRow': menuRoutesEnum.CLIPBOARDS,
  'DiscountCategory': menuRoutesEnum.DISCOUNT_CATEGORIES,
  'Product': menuRoutesEnum.PRODUCTS,
  'Quote': menuRoutesEnum.QUOTES,
  'Subject': menuRoutesEnum.SUBJECTS,
  'SubjectDiscount': menuRoutesEnum.SUBJECT_DISCOUNTS,
  'SupplierBrand': menuRoutesEnum.SUPPLIER_BRANDS,
  'SupplierOrder': menuRoutesEnum.SUPPLIER_ORDERS,
  'Vehicle': menuRoutesEnum.VEHICLES,
  'WarehouseDocument': menuRoutesEnum.WAREHOUSE_DOCUMENTS,
  'Worksheet': menuRoutesEnum.WORKSHEETS,
}

export const getRouterPathFromCacheKey = (internalMessage) => {
  const key = internalMessage?.cache_key
  const identifier_values = internalMessage?.reference_link_data?.identifier_values
  const modelName = key.split('.')[1]
  if (modelName === 'SaleDocument') {
    const docTypeId = Number(identifier_values[2])
    const route = usePortalStore().documentTypePageMap[docTypeId]
    if (route) return route
  }
  return routesByEntityName[modelName]
}

export const getDocumentRouteByDocumentType = (documentTypeId) =>
  usePortalStore().documentTypePageMap[documentTypeId] ?? menuRoutesEnum.SALE_INVOICES

export default router
